// areaOpsBatch — the submit + poll layer for batch area ops ("Toplu Alan
// İşlemleri"). N staged pins go out as ONE POST api/image-editing/area-ops-batch
// (one job, one output image) instead of N chained single-op jobs. The backend
// applies them one by one (or in one AI call, in its single-call mode).
//
// Pure module (no React), same shape as areaOpsSubmit.js: useAreaOpsQueue and
// unit tests call it directly. Always on — no env flag; only a 404 from an
// older backend (no batch route) turns it off for the session.

import { getToken } from "../../../../common/environment_config";
import { extractEditedImageUrls } from "../ImageAnimatorModal";
import { getAreaOpFeature, isAreaOpReady, pollAreaOpJob } from "./areaOpsSubmit";
import { MAX_BATCH_OPS } from "./areaOpsBatchLabel";

export {
  AREA_OPS_BATCH_OPERATION,
  MAX_BATCH_OPS,
  formatAreaOpsBatchLabel,
} from "./areaOpsBatchLabel";

const BATCH_ENDPOINT = "api/image-editing/area-ops-batch";
// 480 × 4s = 32 min, past the batch job's Bull timeout: 30 min in the
// sequential mode (one Gemini call per op, up to 4 tries each), 10 min in the
// single-call mode.
const BATCH_POLL_MAX_ATTEMPTS = 480;

// Set on the first POST 404 (older backend without the route): the batch path
// stays off for the rest of the session and staged ops fall back to the chain.
let batchUnsupported = false;

export function isAreaOpsBatchEnabled() {
  return !batchUnsupported;
}

export function markAreaOpsBatchUnsupported() {
  batchUnsupported = true;
}

// Whole-image features that can join the batch as a step of their own: they
// keep the image's shape, so the boxes of later ops still land right. Resize
// and Benzer İnfografik reshape / regenerate it — they stay out of the batch,
// as does a region feature's "Tüm görsele uygula" mode: they can be listed, and
// run on their own after it (useAreaOpsQueue.applyStaged).
export const STAGEABLE_WHOLE_FEATURE_IDS = new Set(["remove-background", "upscale"]);

// A staged pin goes into the batch (rather than running on its own after it)
// when it could run on its own, except add-logo with
// "tüm görsellere uygula" — that one needs the training-wide endpoint. A
// whole-image pin (`whole`) only when its feature is a stageable step.
export function canStageAreaOp(feature, config, { whole = false } = {}) {
  if (whole) {
    return (
      !!feature &&
      STAGEABLE_WHOLE_FEATURE_IDS.has(feature.id) &&
      isAreaOpReady(feature, config, { whole: true })
    );
  }
  if (!isAreaOpReady(feature, config)) return false;
  return !(feature.id === "add-logo" && config?.applyToAllImages);
}

// Batch op field → pin config key, per type. Flattened from buildAreaOpBody:
// no mode (remove-text / translate), no textChanges[] (change-image-text), no
// useFullImageReference (add-object), and add-logo's upload becomes logoUrl.
const BATCH_FIELDS = {
  "replace-product": {
    referenceImageUrl: "referenceImageUrl",
    targetObject: "targetObject",
    prompt: "prompt",
  },
  "remove-object": { prompt: "prompt" },
  "remove-text": {},
  translate: { targetLanguage: "targetLanguage" },
  "change-image-text": { originalText: "originalText", newText: "newText" },
  "edit-region": { prompt: "prompt" },
  "add-object": { prompt: "prompt" },
  "add-logo": { logoUrl: "referenceImageUrl" },
  // Whole-image steps: no region; upscale's factor is a number (below).
  "remove-background": {},
  upscale: {},
};

const hasText = (v) => typeof v === "string" && v.trim() !== "";

// Pin (or a batchOps snapshot, which carries opId instead of id) → contract op.
// Empty strings are omitted.
export function toBatchOperation({ id, opId, featureId, region, config }) {
  const cfg = config || {};
  const op = { opId: opId ?? id, type: featureId };
  if (region) op.targetRegion = region;
  for (const [field, key] of Object.entries(BATCH_FIELDS[featureId] || {})) {
    if (hasText(cfg[key])) op[field] = cfg[key];
  }
  if (featureId === "replace-product") op.adaptStyle = !!cfg.adaptStyle;
  if (featureId === "upscale") op.scaleFactor = Number(cfg.scaleFactor) || 2;
  return op;
}

// Contract op (e.g. the server's details.operations echo) → pin fields, for
// restaging. userPrompt (the user's original text) wins over prompt.
export function fromBatchOperation(op) {
  const config = {};
  for (const [field, key] of Object.entries(BATCH_FIELDS[op.type] || {})) {
    const value = field === "prompt" && hasText(op.userPrompt) ? op.userPrompt : op[field];
    if (hasText(value)) config[key] = value;
  }
  if (op.type === "replace-product") config.adaptStyle = !!op.adaptStyle;
  if (op.type === "upscale" && op.scaleFactor) config.scaleFactor = op.scaleFactor;
  const restored = { featureId: op.type, region: op.targetRegion || null, config };
  if (!op.targetRegion && STAGEABLE_WHOLE_FEATURE_IDS.has(op.type)) restored.whole = true;
  return restored;
}

// operations[] keeps the given order — it is the marker colour order.
export function buildAreaOpsBatchBody({ ops, imageUrl, ctx }) {
  if (!Array.isArray(ops) || ops.length === 0 || ops.length > MAX_BATCH_OPS) {
    throw new Error(`A batch takes 1-${MAX_BATCH_OPS} operations`);
  }
  const operations = ops.map((op) => {
    const name = op.opId ?? op.id;
    if (!op.region && !op.whole) throw new Error(`Area op ${name}: missing region`);
    if (!canStageAreaOp(getAreaOpFeature(op.featureId), op.config, { whole: !!op.whole })) {
      throw new Error(`Area op ${name}: cannot be batched`);
    }
    return toBatchOperation(op);
  });
  const { trainingId, navId, boxId, sceneIndex } = ctx || {};
  return { imageUrl, idealStudioTrainingId: trainingId, navId, boxId, sceneIndex, operations };
}

function codedError(code, message, data) {
  const err = new Error(message);
  err.code = code;
  if (data) err.data = data;
  return err;
}

const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
  "Content-Type": "application/json",
});

// POST the batch. Returns the jobId.
//   404 → code BATCH_UNSUPPORTED (and the batch path is turned off)
//   insufficient balance → code INSUFFICIENT_BALANCE, .data = server payload
//   400 AREA_OPS_INVALID → same code, .data.errors = [{ opId?, field, message }]
export async function submitAreaOpsBatch({ body, signal }) {
  const apiUrl = process.env.MW_API_URL;
  if (!apiUrl) throw new Error("API URL not configured");

  const res = await fetch(`${apiUrl}${BATCH_ENDPOINT}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal,
  });

  if (res.status === 404) {
    markAreaOpsBatchUnsupported();
    throw codedError("BATCH_UNSUPPORTED", "Batch area ops are not supported");
  }
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    // Same detection as ImageAnimatorModal's submit.
    const rawMsg = (errData?.message || errData?.error?.message || "").toString().toLowerCase();
    if (rawMsg.includes("insufficient.balance") || rawMsg.includes("insufficient balance")) {
      throw codedError("INSUFFICIENT_BALANCE", "Insufficient balance", errData);
    }
    const message = errData?.message || errData?.error?.message || "Job submit failed";
    // Names the ops at fault, so each box can say what is wrong with it.
    if (errData?.code === "AREA_OPS_INVALID") throw codedError(errData.code, message, errData);
    throw new Error(message);
  }

  const json = await res.json().catch(() => ({}));
  if (!json?.jobId) throw new Error("No jobId returned");
  return json.jobId;
}

// Completed job → { url, partial, ops, steps }. A partial run is still
// "completed"; ops = [{ opId, type, status: "done"|"failed", error? }].
// steps = the intermediate versions of a sequential batch, in order — each op
// that changed the image, [{ opId, type, status, url }], the result itself
// excluded ([] for a single-call batch).
export function parseBatchJobResult(jobData) {
  const result = jobData?.result && typeof jobData.result === "object" ? jobData.result : {};
  const url = result.url || extractEditedImageUrls(jobData)[0] || null;
  if (!url) throw new Error("No result image URL");
  const details = result.details || {};
  const steps = Array.isArray(details.steps)
    ? details.steps
        .filter((s) => s && s.url && s.url !== url)
        .map((s) => ({ opId: s.opId, type: s.type, status: s.status, url: s.url }))
    : [];
  return {
    url,
    partial: !!details.partial,
    ops: Array.isArray(details.ops) ? details.ops : [],
    steps,
  };
}

export function pollBatchJob(
  jobId,
  { signal, onProgress, maxAttempts = BATCH_POLL_MAX_ATTEMPTS } = {}
) {
  return pollAreaOpJob(jobId, {
    signal,
    onProgress,
    resolveResult: parseBatchJobResult,
    maxAttempts,
  });
}

// Build + submit + poll in one shot. Resolves { jobId, url, partial, ops }.
export async function runAreaOpsBatch({ ops, imageUrl, ctx, signal, onJobId, onProgress }) {
  const body = buildAreaOpsBatchBody({ ops, imageUrl, ctx });
  const jobId = await submitAreaOpsBatch({ body, signal });
  onJobId?.(jobId);
  const result = await pollBatchJob(jobId, { signal, onProgress });
  return { jobId, ...result };
}

// Fold the server's per-op outcome into the batch pin's op snapshots (by opId).
// An op the server did not report failed on a partial run, succeeded otherwise.
export function mergeBatchOpStatuses(batchOps, resultOps, partial) {
  const byId = new Map((resultOps || []).map((r) => [r.opId, r]));
  return (batchOps || []).map((op) => {
    const r = byId.get(op.opId);
    return {
      ...op,
      status: r?.status || (partial ? "failed" : "done"),
      error: r?.error || null,
    };
  });
}

// Credit estimate for the staged list: N >= 2 → the batch credit route, N == 1
// → the op's own legacy route (that is what "Uygula (1)" calls). Resolves
// { requiredCredits, availableCredits, hasSufficientBalance } or null when the
// estimate is unavailable (non-OK, e.g. an old backend).
export async function estimateAreaOpsCredit(stagedPins, { signal } = {}) {
  const apiUrl = process.env.MW_API_URL;
  if (!apiUrl || !stagedPins?.length) return null;

  let endpoint = `${BATCH_ENDPOINT}/credit`;
  let body = {
    count: stagedPins.length,
    operations: stagedPins.map((p) => ({ type: p.featureId })),
  };
  if (stagedPins.length === 1) {
    const feature = getAreaOpFeature(stagedPins[0].featureId);
    if (!feature) return null;
    endpoint = `${feature.endpoint}/credit`;
    body = {};
  }

  const res = await fetch(`${apiUrl}${endpoint}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}
